import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, fecha, hoy, buscar } from '../utils/formato';

// Clientes y cartera.
//
// La pantalla abre por la cartera y no por la lista de clientes, porque la
// pregunta que uno se hace al llegar al negocio no es «quiénes son mis
// clientes» sino «a quién tengo que cobrarle hoy».
//
// Por eso se ordena por el más atrasado, no alfabéticamente, y la fecha que se
// muestra es la de la deuda más vieja sin cubrir: un saldo sin fecha no dice a
// quién llamar primero.

const VACIO = { nombre: '', documento: '', telefono: '', direccion: '', nota: '' };

// A partir de cuántos días se considera atrasado. No es una regla contable:
// es el punto en que conviene levantar el teléfono.
const DIAS_ATRASO = 30;

function Clientes({ onCambio }) {
  const [cartera, setCartera] = useState({ clientes: [], total: 0 });
  const [clientes, setClientes] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [pestana, setPestana] = useState('cartera');

  const [editando, setEditando] = useState(null);
  const [abonando, setAbonando] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);

  const cargar = useCallback(() => {
    axios.get('/api/clientes/cartera').then(r => setCartera(r.data || { clientes: [], total: 0 })).catch(() => {});
    axios.get('/api/clientes').then(r => setClientes(r.data || [])).catch(() => {});
  }, []);

  useEffect(cargar, [cargar]);

  const guardarCliente = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      if (editando.id) await axios.put(`/api/clientes/${editando.id}`, editando);
      else await axios.post('/api/clientes', editando);
      setAviso({ tipo: 'bueno', texto: `${editando.nombre} quedó guardado` });
      setEditando(null);
      cargar();
      onCambio && onCambio();
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo guardar' });
    }
    setGuardando(false);
  };

  const guardarAbono = async (e) => {
    e.preventDefault();
    const monto = parseFloat(abonando.monto);
    if (!(monto > 0)) return setAviso({ tipo: 'malo', texto: 'El abono tiene que ser mayor que cero' });
    setGuardando(true);
    try {
      await axios.post('/api/abonos', {
        cliente_id: abonando.cliente.id,
        fecha: abonando.fecha, monto,
        forma_pago: abonando.forma_pago, nota: abonando.nota || null,
      });
      const queda = abonando.cliente.saldo - monto;
      setAviso({
        tipo: 'bueno',
        texto: queda > 0
          ? `${abonando.cliente.nombre} abonó ${plata(monto)} — le quedan debiendo ${plata(queda)}`
          : `${abonando.cliente.nombre} abonó ${plata(monto)} y quedó al día`,
      });
      setAbonando(null);
      cargar();
      onCambio && onCambio();
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo guardar el abono' });
    }
    setGuardando(false);
  };

  const verDetalle = async (id) => {
    try {
      const { data } = await axios.get(`/api/clientes/${id}/estado`);
      setDetalle(data);
    } catch {
      setAviso({ tipo: 'malo', texto: 'No se pudo cargar el detalle' });
    }
  };

  const visibles = buscar(clientes, busqueda, ['nombre', 'documento', 'telefono']);
  const atrasados = cartera.clientes.filter(c => (c.dias || 0) >= DIAS_ATRASO);

  return (
    <>
      <div className="encabezado">
        <h1>Clientes</h1>
        <p>Quién debe, desde cuándo, y a quién hay que cobrarle</p>
      </div>

      {aviso && <div className={'aviso ' + aviso.tipo}>{aviso.texto}</div>}

      <div className="rejilla">
        <div className="dato" style={{ '--color': 'var(--ojo)' }}>
          <div className="rotulo">💰 Por cobrar</div>
          <div className="valor cifra">{plata(cartera.total)}</div>
          <div className="pie">
            {cartera.clientes.length === 1 ? '1 cliente debe'
              : `${cartera.clientes.length} clientes deben`}
          </div>
        </div>
        <div className="dato" style={{ '--color': atrasados.length ? 'var(--malo)' : 'var(--bueno)' }}>
          <div className="rotulo">⏰ Atrasados</div>
          <div className="valor cifra">{atrasados.length}</div>
          <div className="pie">más de {DIAS_ATRASO} días</div>
        </div>
        <div className="dato" style={{ '--color': 'var(--acero)' }}>
          <div className="rotulo">👥 Clientes</div>
          <div className="valor cifra">{clientes.length}</div>
          <div className="pie">registrados</div>
        </div>
      </div>

      <div className="grupo">
        <button className={'opcion' + (pestana === 'cartera' ? ' activa' : '')}
                onClick={() => setPestana('cartera')}>
          💰 Cartera
        </button>
        <button className={'opcion' + (pestana === 'todos' ? ' activa' : '')}
                onClick={() => setPestana('todos')}>
          👥 Todos los clientes
        </button>
      </div>

      {/* ── Cartera ─────────────────────────────────────────────────────── */}
      {pestana === 'cartera' && (
        <div className="bloque">
          <h2>A quién cobrarle
            {cartera.clientes.length > 0 &&
              <span className="contador">{cartera.clientes.length}</span>}
          </h2>
          {cartera.clientes.length === 0 ? (
            <div className="vacio">
              Nadie te debe nada. Cuando vendas a crédito, aquí aparece quién y desde cuándo.
            </div>
          ) : (
            <div className="tabla-caja">
              <table className="datos">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th style={{ textAlign: 'right' }}>Le fié</th>
                    <th style={{ textAlign: 'right' }}>Ha abonado</th>
                    <th style={{ textAlign: 'right' }}>Debe</th>
                    <th>Desde</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {cartera.clientes.map(c => {
                    const tarde = (c.dias || 0) >= DIAS_ATRASO;
                    return (
                      <tr key={c.id}>
                        <td className="recio">
                          {c.nombre}
                          {c.telefono && (
                            <div className="cifra" style={{ fontSize: 11, fontWeight: 400,
                                                            color: 'var(--tinta-tenue)' }}>
                              📞 {c.telefono}
                            </div>
                          )}
                        </td>
                        <td className="num">{plata(c.fiado)}</td>
                        <td className="num">{plata(c.abonado)}</td>
                        <td className="num recio"
                            style={{ color: tarde ? 'var(--malo)' : undefined }}>
                          {plata(c.saldo)}
                        </td>
                        <td>
                          <span className={'pinta ' + (tarde ? 'malo' : 'gris')}>
                            {c.dias} {c.dias === 1 ? 'día' : 'días'}
                          </span>
                          <div style={{ fontSize: 11, color: 'var(--tinta-tenue)', marginTop: 3 }}>
                            {fecha(c.desde)}
                          </div>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button className="boton chico"
                                  onClick={() => setAbonando({
                                    cliente: c, fecha: hoy(), monto: '',
                                    forma_pago: 'efectivo', nota: '',
                                  })}>
                            💵 Abonar
                          </button>{' '}
                          <button className="boton chico suave" onClick={() => verDetalle(c.id)}>
                            Ver
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Todos los clientes ──────────────────────────────────────────── */}
      {pestana === 'todos' && (
        <div className="bloque">
          <h2>Todos los clientes <span className="contador">{clientes.length}</span></h2>
          <div className="cuerpo">
            <div className="acciones" style={{ marginBottom: 14 }}>
              <button className="boton" onClick={() => setEditando({ ...VACIO })}>
                ➕ Nuevo cliente
              </button>
            </div>

            <div className="buscador">
              <input value={busqueda} onChange={e => setBusqueda(e.target.value)}
                     placeholder="🔎 Buscar por nombre, cédula o teléfono" />
              {busqueda && <button type="button" onClick={() => setBusqueda('')}>×</button>}
            </div>

            {visibles.length === 0 ? (
              <div className="vacio">
                {clientes.length === 0
                  ? 'Todavía no hay clientes. Crea el primero para poder fiarle.'
                  : 'Ningún cliente coincide con esa búsqueda.'}
              </div>
            ) : (
              <div className="tabla-caja">
                <table className="datos">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Cédula</th>
                      <th>Teléfono</th>
                      <th>Dirección</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibles.map(c => (
                      <tr key={c.id}>
                        <td className="recio">{c.nombre}</td>
                        <td className="codigo">{c.documento || '—'}</td>
                        <td>{c.telefono || '—'}</td>
                        <td style={{ fontSize: 13 }}>{c.direccion || '—'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button className="boton chico suave"
                                  onClick={() => setEditando({ ...VACIO, ...c })}>
                            ✏️
                          </button>{' '}
                          <button className="boton chico suave" onClick={() => verDetalle(c.id)}>
                            Ver
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Crear o editar cliente ──────────────────────────────────────── */}
      {editando && (
        <div className="bloque">
          <h2>{editando.id ? '✏️ Editar cliente' : '➕ Nuevo cliente'}</h2>
          <form onSubmit={guardarCliente}>
            <div className="cuerpo">
              <div className="campos">
                <label className="campo">
                  <span>Nombre *</span>
                  <input value={editando.nombre} required autoFocus
                         onChange={e => setEditando({ ...editando, nombre: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Cédula o NIT</span>
                  <input value={editando.documento || ''}
                         onChange={e => setEditando({ ...editando, documento: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Teléfono</span>
                  <input value={editando.telefono || ''}
                         onChange={e => setEditando({ ...editando, telefono: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Dirección</span>
                  <input value={editando.direccion || ''}
                         onChange={e => setEditando({ ...editando, direccion: e.target.value })} />
                </label>
              </div>
              <label className="campo" style={{ marginTop: 12 }}>
                <span>Nota</span>
                <input value={editando.nota || ''} placeholder="Lo que convenga recordar"
                       onChange={e => setEditando({ ...editando, nota: e.target.value })} />
              </label>
              <div className="acciones">
                <button type="button" className="boton suave" onClick={() => setEditando(null)}>
                  Cancelar
                </button>
                <button className="boton" disabled={guardando}>
                  {guardando ? 'Guardando…' : '💾 Guardar'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* ── Registrar abono ─────────────────────────────────────────────── */}
      {abonando && (
        <div className="bloque">
          <h2>💵 Abono de {abonando.cliente.nombre}</h2>
          <form onSubmit={guardarAbono}>
            <div className="cuerpo">
              <div className="aviso ojo" style={{ marginBottom: 16 }}>
                Debe <strong>{plata(abonando.cliente.saldo)}</strong> desde el{' '}
                {fecha(abonando.cliente.desde)} ({abonando.cliente.dias} días).
              </div>
              <div className="campos">
                <label className="campo">
                  <span>Cuánto abonó *</span>
                  <input type="number" step="1" min="0" className="num-grande" required autoFocus
                         value={abonando.monto} placeholder="0"
                         onChange={e => setAbonando({ ...abonando, monto: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Fecha</span>
                  <input type="date" value={abonando.fecha}
                         onChange={e => setAbonando({ ...abonando, fecha: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Cómo pagó</span>
                  <select value={abonando.forma_pago}
                          onChange={e => setAbonando({ ...abonando, forma_pago: e.target.value })}>
                    <option value="efectivo">💵 Efectivo</option>
                    <option value="transferencia">🏦 Transferencia</option>
                  </select>
                </label>
              </div>
              {parseFloat(abonando.monto) > 0 && (
                <div style={{ marginTop: 14, fontSize: 14 }}>
                  Le quedarían debiendo{' '}
                  <strong className="cifra">
                    {plata(Math.max(0, abonando.cliente.saldo - parseFloat(abonando.monto)))}
                  </strong>
                  {parseFloat(abonando.monto) > abonando.cliente.saldo && (
                    <span className="pinta ojo" style={{ marginLeft: 8 }}>
                      está abonando más de lo que debe
                    </span>
                  )}
                </div>
              )}
              <div className="acciones">
                <button type="button" className="boton suave" onClick={() => setAbonando(null)}>
                  Cancelar
                </button>
                <button className="boton" disabled={guardando}>
                  {guardando ? 'Guardando…' : '💾 Registrar abono'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* ── Detalle de un cliente ───────────────────────────────────────── */}
      {detalle && (
        <div className="bloque">
          <h2>📄 {detalle.cliente.nombre}</h2>
          <div className="cuerpo">
            <div className="rejilla">
              <div className="dato" style={{ '--color': 'var(--acero)' }}>
                <div className="rotulo">Le fié</div>
                <div className="valor cifra">{plata(detalle.fiado)}</div>
              </div>
              <div className="dato" style={{ '--color': 'var(--bueno)' }}>
                <div className="rotulo">Ha abonado</div>
                <div className="valor cifra">{plata(detalle.abonado)}</div>
              </div>
              <div className="dato" style={{ '--color': detalle.saldo > 0 ? 'var(--ojo)' : 'var(--bueno)' }}>
                <div className="rotulo">Debe</div>
                <div className="valor cifra">{plata(detalle.saldo)}</div>
              </div>
            </div>

            <h3 style={{ marginTop: 20, fontSize: 15 }}>Sus compras</h3>
            {detalle.ventas.length === 0 ? (
              <div className="vacio">Todavía no le ha comprado nada.</div>
            ) : (
              <div className="tabla-caja">
                <table className="datos">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Cómo pagó</th>
                      <th style={{ textAlign: 'right' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalle.ventas.map(v => (
                      <tr key={v.id} style={{ opacity: v.anulada ? 0.45 : 1 }}>
                        <td>{fecha(v.fecha)}</td>
                        <td>
                          <span className={'pinta ' + (v.forma_pago === 'credito' ? 'ojo' : 'bueno')}>
                            {v.forma_pago}
                          </span>
                          {v.anulada ? <span className="pinta malo" style={{ marginLeft: 6 }}>anulada</span> : null}
                        </td>
                        <td className="num recio">{plata(v.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <h3 style={{ marginTop: 20, fontSize: 15 }}>Sus abonos</h3>
            {detalle.abonos.length === 0 ? (
              <div className="vacio">No ha abonado todavía.</div>
            ) : (
              <div className="tabla-caja">
                <table className="datos">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Cómo pagó</th>
                      <th>Nota</th>
                      <th style={{ textAlign: 'right' }}>Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalle.abonos.map(a => (
                      <tr key={a.id}>
                        <td>{fecha(a.fecha)}</td>
                        <td>{a.forma_pago}</td>
                        <td style={{ fontSize: 13 }}>{a.nota || '—'}</td>
                        <td className="num recio">{plata(a.monto)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="acciones">
              <button className="boton suave" onClick={() => setDetalle(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Clientes;
